// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { useCallback, useMemo, useState } from 'react';

/**
 * Reusable hook for managing bulk selection of list items.
 *
 * Tracks a set of selected IDs and provides helpers for
 * toggling individual items, selecting/deselecting all,
 * and querying selection state.
 */
export function useBulkSelection(allIds: string[]) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(allIds));
  }, [allIds]);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.size === allIds.length ? new Set() : new Set(allIds),
    );
  }, [allIds]);

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds],
  );

  const count = selectedIds.size;
  const allSelected = allIds.length > 0 && count === allIds.length;
  const someSelected = count > 0 && count < allIds.length;

  /** IDs as an array — stable when the set hasn't changed. */
  const selectedArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

  return {
    selectedIds,
    selectedArray,
    count,
    allSelected,
    someSelected,
    noneSelected: count === 0,
    toggle,
    selectAll,
    deselectAll,
    toggleAll,
    isSelected,
  };
}
